import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const PaymentCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'pending', 'failed'
  const [bookingId, setBookingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let pollInterval;
    let timeout;
    
    const processPaymentReturn = async () => {
      // Get booking ID from sessionStorage or URL
      const storedBookingId = sessionStorage.getItem('pending_booking_id');
      const urlBookingId = searchParams.get('booking_id');
      const bid = storedBookingId || urlBookingId;
      
      // Check for reference_no (from mock payment) or status parameter
      const referenceNo = searchParams.get('reference_no');
      const urlStatus = searchParams.get('status');
      
      console.log('Payment callback params:', { bid, referenceNo, urlStatus, storedBookingId });

      if (!bid) {
        setStatus('failed');
        setError('No booking information found');
        return 'failed';
      }

      setBookingId(bid);
      
      // If status=cancelled was returned
      if (urlStatus === 'cancelled') {
        setStatus('failed');
        setError('Payment was cancelled');
        return 'failed';
      }

      // If we have a reference_no, it means payment was completed on mock/production
      // We should confirm the payment on our backend
      if (referenceNo) {
        try {
          console.log('Confirming payment with reference:', referenceNo);
          const confirmResponse = await fetch(`${API}/payments/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              booking_id: bid,
              reference_no: referenceNo
            })
          });
          
          if (confirmResponse.ok) {
            setStatus('success');
            sessionStorage.removeItem('pending_booking_id');
            sessionStorage.removeItem('pending_transaction_id');
            return 'success';
          }
        } catch (err) {
          console.error('Error confirming payment:', err);
        }
      }

      // Fall back to checking payment status
      try {
        const response = await fetch(`${API}/payments/status/${bid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.payment_status === 'paid' || data.status === 'confirmed') {
            setStatus('success');
            sessionStorage.removeItem('pending_booking_id');
            sessionStorage.removeItem('pending_transaction_id');
            return 'success';
          } else if (data.payment_status === 'pending') {
            setStatus('pending');
            return 'pending';
          } else {
            setStatus('failed');
            setError('Payment was not completed');
            return 'failed';
          }
        } else {
          setStatus('failed');
          setError('Failed to verify payment status');
          return 'failed';
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        setStatus('failed');
        setError('Failed to verify payment');
        return 'failed';
      }
    };

    // Process payment return
    processPaymentReturn().then(initialStatus => {
      // Poll for status updates (payment webhook might take a moment) - only if still pending or checking
      pollInterval = setInterval(async () => {
        const currentStatus = await processPaymentReturn();
        if (currentStatus === 'success' || currentStatus === 'failed') {
          clearInterval(pollInterval);
        }
      }, 3000);
      
      // Stop polling after 30 seconds
      timeout = setTimeout(() => {
        clearInterval(pollInterval);
        setStatus(prevStatus => prevStatus === 'checking' ? 'pending' : prevStatus);
      }, 30000);
    });

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeout) clearTimeout(timeout);
    };
  }, [token, searchParams]);


  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center space-y-4">
            <Loader2 className="h-16 w-16 mx-auto text-primary animate-spin" />
            <h2 className="text-2xl font-bold">Verifying Payment...</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment.
            </p>
          </div>
        );

      case 'success':
        // Redirect to success payment page
        navigate(`/success-payment?booking_id=${bookingId}`);
        return (
          <div className="text-center space-y-4">
            <Loader2 className="h-16 w-16 mx-auto text-green-600 animate-spin" />
            <h2 className="text-2xl font-bold text-green-600">Payment Successful!</h2>
            <p className="text-muted-foreground">
              Redirecting to your receipt...
            </p>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center space-y-4">
            <div className="h-20 w-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="h-12 w-12 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-amber-600">Payment Processing</h2>
            <p className="text-muted-foreground">
              Your payment is being processed. This may take a few moments.
            </p>
            <p className="text-sm text-muted-foreground">
              If payment was completed, please wait for confirmation. 
              You can also check your bookings page later.
            </p>
            <div className="pt-4 space-y-2">
              <Button onClick={() => navigate('/my-bookings')} className="w-full">
                Check My Bookings
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                <Loader2 className="h-4 w-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center space-y-4">
            <div className="h-20 w-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-600">Payment Failed</h2>
            <p className="text-muted-foreground">
              {error || 'We were unable to process your payment.'}
            </p>
            <p className="text-sm text-muted-foreground">
              Please try again or contact support if the problem persists.
            </p>
            <div className="pt-4 space-y-2">
              <Button onClick={() => navigate(-1)} className="w-full">
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                Back to Home
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Payment Status</CardTitle>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentCallbackPage;
